import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Shield, Baby, Check, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { api } from '../../App';
import { toast } from 'sonner';

const money = (p) => `£${Number(p).toFixed(Number.isInteger(Number(p)) ? 0 : 2)}`;

/** Self-service renewal: verify (number + DOB) -> see current expiry and the children who will continue -> pay. */
export default function LuxuryRenew() {
  const [form, setForm] = useState({ membership_number: '', dob: '' });
  const [info, setInfo] = useState(null);
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.membership_number.trim() || !form.dob) { toast.error('Please enter your membership number and date of birth'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/membership/renew/verify', { membership_number: form.membership_number.trim().toUpperCase(), dob: form.dob });
      setInfo(res); setPlan(res.default_type || (res.plans[0] && res.plans[0].type) || '');
      toast.success(`Welcome back, ${res.member.full_name}`);
    } catch (err) { toast.error(err.message || 'Verification failed', { duration: 8000 }); }
    finally { setLoading(false); }
  };

  const handleRenew = async () => {
    if (!plan) { toast.error('Please choose a membership option'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/membership/renew', { membership_number: form.membership_number.trim().toUpperCase(), dob: form.dob, membership_type: plan, origin_url: window.location.origin });
      toast.success(res.message);
      if (res.url) window.location.href = res.url;
    } catch (err) { toast.error(err.message || 'Could not start renewal', { duration: 8000 }); }
    finally { setLoading(false); }
  };

  const selected = info?.plans?.find(p => p.type === plan);
  const following = (info?.children || []).filter(c => c.follows);
  const agedOut = (info?.children || []).filter(c => !c.follows);
  const expired = info?.member?.live_status === 'EXPIRED';

  return (
    <div className="overflow-hidden" data-testid="renew-page">
      <section className="relative pt-24 pb-12 bg-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]"><RefreshCw className="w-3 h-3 mr-1" /> Membership Renewal</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--lux-royal))] mb-4" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="renew-title">
              Renew your <span className="text-[hsl(var(--lux-gold))]">Membership</span>
            </h1>
            <p className="text-[hsl(var(--lux-ink)/0.7)] max-w-2xl mx-auto">
              Thank you for continuing your journey with us. Your membership number and card stay exactly the same, and any children under 14 on your membership continue alongside you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {!info ? (
            <Card className="lux-ornate-card"><CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4 text-[hsl(var(--lux-royal))]"><Shield className="w-5 h-5" /><h2 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond' }}>Verify your membership</h2></div>
              <form onSubmit={handleVerify} className="space-y-5">
                <div><Label>Membership Number *</Label><Input required placeholder="e.g. GSNG092612345" value={form.membership_number} onChange={e => setForm({ ...form, membership_number: e.target.value })} data-testid="renew-membership-input" /></div>
                <div><Label>Date of Birth *</Label><Input type="date" required max={new Date().toISOString().split('T')[0]} value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} data-testid="renew-dob-input" /></div>
                <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-11" data-testid="renew-verify-button">{loading ? 'Verifying...' : 'Verify & Continue'}</Button>
                <p className="text-xs text-center text-[hsl(var(--lux-ink)/0.55)]">Not a member yet? <a href="/membership" className="text-[hsl(var(--lux-royal))] underline">Join here</a>.</p>
              </form>
            </CardContent></Card>
          ) : (
            <Card className="lux-ornate-card"><CardContent className="p-8 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>Namaste, {info.member.full_name}</h2>
                <p className="text-sm text-[hsl(var(--lux-ink)/0.65)]" data-testid="renew-member-summary">
                  {info.member.membership_number} · {info.member.type_label} · {expired ? <span className="text-[hsl(var(--lux-vermilion))] font-medium">expired {info.member.expires_at}</span> : <>valid until <span className="font-medium">{info.member.expires_at}</span></>}
                </p>
                {!expired && <p className="text-xs text-[hsl(var(--lux-ink)/0.6)] mt-1">Renewing early? Your remaining time is kept — the new period is added on top.</p>}
              </div>

              <div>
                <Label className="mb-2 block">Choose your membership</Label>
                <div className="grid gap-3" data-testid="renew-plans">
                  {info.plans.map(p => (
                    <button key={p.type} type="button" onClick={() => setPlan(p.type)} className={`text-left rounded-xl border p-4 transition-colors ${plan === p.type ? 'border-[hsl(var(--lux-royal))] bg-white' : 'border-[hsl(var(--lux-gold)/0.3)] hover:bg-white/60'}`} data-testid={`renew-plan-${p.type}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div><div className="font-semibold text-[hsl(var(--lux-royal))]">{p.label}</div><div className="text-xs text-[hsl(var(--lux-ink)/0.6)]">{p.description}</div></div>
                        <div className="text-right shrink-0"><div className="font-bold text-lg text-[hsl(var(--lux-royal))]">{money(p.price)}</div><div className="text-[11px] text-[hsl(var(--lux-ink)/0.55)]">{p.validity_days ? p.validity_label : 'Lifetime'}</div></div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {(following.length > 0 || agedOut.length > 0) && (
                <div className="rounded-xl border border-[hsl(var(--lux-gold)/0.4)] bg-white p-4 text-sm space-y-2" data-testid="renew-children">
                  {following.length > 0 && (
                    <>
                      <div className="font-medium text-[hsl(var(--lux-royal))] flex items-center gap-1.5"><Baby className="w-4 h-4" /> Continuing with your membership</div>
                      <ul className="space-y-1">{following.map(c => <li key={c.membership_number} className="flex items-center justify-between gap-2" data-testid={`renew-child-${c.membership_number}`}><span>{c.full_name} · age {c.age}</span><span className="text-xs text-[hsl(var(--lux-ink)/0.6)]">until {c.new_expiry}</span></li>)}</ul>
                      <p className="text-xs text-[hsl(var(--lux-ink)/0.6)]">Children under 14 remain complimentary and keep their own cards — nothing further is needed.</p>
                    </>
                  )}
                  {agedOut.length > 0 && (
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.7)] pt-1" data-testid="renew-children-aged-out">
                      {agedOut.map(c => c.full_name).join(', ')} {agedOut.length === 1 ? 'is' : 'are'} now 14 or over and would be very welcome to <a href="/membership/add-family" className="text-[hsl(var(--lux-royal))] underline">join as a member in their own right</a>.
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-lg border border-[hsl(var(--lux-gold)/0.4)] bg-white p-4 text-sm flex items-center justify-between" data-testid="renew-summary">
                <span>{selected ? `${selected.label} · new expiry ${selected.validity_days ? info.preview_new_expiry || '' : 'lifetime'}` : 'Select an option above'}</span>
                <span className="font-semibold text-[hsl(var(--lux-royal))]" data-testid="renew-total">{selected ? money(selected.price) : '—'}</span>
              </div>

              <Button onClick={handleRenew} disabled={loading || !plan} className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg" data-testid="renew-pay-button">
                <CreditCard className="w-5 h-5 mr-2" /> {loading ? 'Processing...' : `Renew now${selected ? ` · ${money(selected.price)}` : ''}`}
              </Button>
              <p className="text-xs text-center text-[hsl(var(--lux-ink)/0.55)] flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Secure card payment · same membership number · same card</p>
            </CardContent></Card>
          )}
        </div>
      </section>
    </div>
  );
}
